'use client'

import { CalendarIcon, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { DateRange } from 'react-day-picker'

import { Calendar } from './Calendar'
import { Button, buttonVariants, Popover, PopoverContent, PopoverTrigger } from '@fp/ui-kit'
import { cn } from '@lib/cn'
import { dayjs, getAppTimezone } from '@lib/dayjs'
import { iconStroke } from '@lib/iconProps'
import { backendIsoToPickerDate, pickerDateToBackendIso, tzDayjsToPickerDate, type IsoDateRange } from '@lib/tz'
import { useAppSelector } from '@store/hooks'
import { selectCurrentUserTimezone } from '@store/modules/session/slice'

const buildRangePresets = (tz: string) => {
  const today = dayjs().tz(tz)

  return [
    {
      label: 'Today',
      value: 'today',
      range: [today, today],
    },
    {
      label: 'Yesterday',
      value: 'yesterday',
      range: [today.subtract(1, 'day'), today.subtract(1, 'day')],
    },
    {
      label: 'This Week',
      value: 'thisWeek',
      range: [today.startOf('week'), today.endOf('week').isAfter(today) ? today : today.endOf('week')],
    },
    {
      label: 'Last Week',
      value: 'lastWeek',
      range: [today.subtract(1, 'week').startOf('week'), today.subtract(1, 'week').endOf('week')],
    },
    {
      label: 'This Month',
      value: 'thisMonth',
      range: [today.startOf('month'), today.endOf('month').isAfter(today) ? today : today.endOf('month')],
    },
    {
      label: 'Last Month',
      value: 'lastMonth',
      range: [today.subtract(1, 'month').startOf('month'), today.subtract(1, 'month').endOf('month')],
    },
    {
      label: 'This Year',
      value: 'thisYear',
      range: [today.startOf('year'), today.endOf('year').isAfter(today) ? today : today.endOf('year')],
    },
  ]
}

const toPickerRange = (iso?: IsoDateRange): DateRange | undefined => {
  if (!iso?.from && !iso?.to) return undefined

  return {
    from: iso.from ? backendIsoToPickerDate(iso.from) : undefined,
    to: iso.to ? backendIsoToPickerDate(iso.to) : undefined,
  }
}

const fromPickerRange = (range?: DateRange): IsoDateRange | undefined => {
  if (!range?.from && !range?.to) return undefined

  return {
    from: range.from ? pickerDateToBackendIso(range.from) : undefined,
    to: range.to ? pickerDateToBackendIso(range.to) : undefined,
  }
}

type Props = {
  value?: IsoDateRange
  onChange?: (range: IsoDateRange | undefined) => void
  onSuccess?: (range: IsoDateRange | undefined) => void
  onReset?: () => void
  className?: string
  showPresets?: boolean
}

export const DatePicker = ({ value, onChange, onSuccess, className, onReset, showPresets = true }: Props) => {
  const initialRange = useMemo(() => toPickerRange(value), [value])
  const [date, setDate] = useState<DateRange | undefined>(initialRange)
  const [selectedPreset, setSelectedPreset] = useState<string | undefined>(undefined)

  const userTimezone = useAppSelector(selectCurrentUserTimezone)
  const tz = userTimezone || getAppTimezone()

  const rangePresets = useMemo(() => buildRangePresets(tz), [tz])

  const handlePresetClick = useCallback(
    (presetValue: string) => {
      if (!showPresets) return

      const preset = rangePresets.find((p) => p.value === presetValue)
      if (preset) {
        const from = preset.range[0]
        const to = preset.range[1]
        const range: DateRange = {
          from: from ? tzDayjsToPickerDate(from.startOf('day')) : undefined,
          to: to ? tzDayjsToPickerDate(to.endOf('day')) : undefined,
        }
        setDate(range)
        setSelectedPreset(presetValue)
        onChange?.(fromPickerRange(range))
      }
    },
    [onChange, showPresets, rangePresets],
  )

  useEffect(() => {
    const next = toPickerRange(value)
    setDate(next)

    if (!showPresets) return

    if (!next?.from || !next?.to) {
      setSelectedPreset(undefined)

      return
    }

    const matchedPreset = rangePresets.find((preset) => {
      const from = preset.range[0] ? tzDayjsToPickerDate(preset.range[0]) : undefined
      const to = preset.range[1] ? tzDayjsToPickerDate(preset.range[1]) : undefined

      return from && to && dayjs(from).isSame(next.from, 'day') && dayjs(to).isSame(next.to, 'day')
    })

    setSelectedPreset(matchedPreset?.value)
  }, [value, showPresets, rangePresets])

  const handleCalendarSelect = useCallback(
    (range: DateRange | undefined) => {
      const adjusted = range?.to ? { ...range, to: dayjs(range.to).endOf('day').toDate() } : range
      setDate(adjusted)
      if (showPresets) {
        setSelectedPreset(undefined)
      }
      onChange?.(fromPickerRange(adjusted))
    },
    [onChange, showPresets],
  )

  const handleApply = useCallback(() => {
    onSuccess?.(fromPickerRange(date))
  }, [onSuccess, date])

  const onClear = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      setDate(undefined)
      onChange?.(undefined)
    },
    [onChange],
  )

  const displayValue = date?.from
    ? date.to
      ? `${dayjs(date.from).format('YYYY-MM-DD')} - ${dayjs(date.to).format('YYYY-MM-DD')}`
      : dayjs(date.from).format('YYYY-MM-DD')
    : 'Select dates'

  return (
    <div className={cn('flex gap-2 items-center', className)}>
      <Popover>
        <PopoverTrigger asChild>
          <div
            className={cn(
              buttonVariants({ variant: 'outline' }),
              'w-[260px] justify-start text-left font-normal border-input min-h-9.5 group px-2',
              !date && 'text-muted-foreground',
            )}
          >
            <div className='flex justify-start items-center w-full'>
              <CalendarIcon className='mr-2 h-4 w-4 text-muted-foreground' strokeWidth={iconStroke.bold} />
              <div className={date?.from ? 'text-foreground' : 'text-muted-foreground'}>{displayValue}</div>
              {date && (
                <Button
                  onClick={onClear}
                  variant='ghost'
                  size='icon'
                  className='ml-auto cursor-pointer text-muted-foreground hover:text-foreground size-5!'
                >
                  <X className='size-4!' />
                </Button>
              )}
            </div>
          </div>
        </PopoverTrigger>

        <PopoverContent className='w-auto p-4 pl-0 min-h-[365.2px]' align='start' forceMount asChild>
          <div className='flex '>
            {showPresets && (
              <div className='flex flex-col gap-2 min-w-[150px]'>
                {rangePresets.map((preset) => (
                  <Button
                    key={preset.value}
                    variant={preset.value === selectedPreset ? 'primary' : 'ghost'}
                    className='font-normal w-full rounded-none'
                    onClick={() => handlePresetClick(preset.value)}
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
            )}

            {showPresets && <div className='mr-4 border-l border-input' />}

            <div className='flex flex-col gap-4'>
              <Calendar
                mode='range'
                numberOfMonths={2}
                selected={date}
                onSelect={handleCalendarSelect}
                defaultMonth={new Date(new Date().setMonth(new Date().getMonth() - 1))}
              />
              {onSuccess && (
                <Button onClick={handleApply} variant='primary'>
                  Apply
                </Button>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {onReset && (
        <Button variant='outline' onClick={onReset}>
          Clear
        </Button>
      )}
    </div>
  )
}
